/**
 * Who changed what, and whether they said why.
 *
 * `changedFields` IS THE HEADLINE, not the note. The note is free text an
 * operator may or may not have written; the field list is what the server
 * recorded, and it is the only part of a row that cannot be wrong. A missing
 * note is rendered as an explicit absence ("No reason recorded") rather than as
 * whitespace, because a blank line reads as a rendering bug and an operator who
 * suspects a rendering bug stops trusting the whole screen.
 *
 * THE FULL SNAPSHOT IS BEHIND A CLICK. Each row carries the entire params row as
 * it stood after the write — thirty-odd fields — and rendering that inline turns
 * a scannable history into a wall. Opening it is the answer to the question this
 * page exists for: not "what changed" but "what was it then".
 *
 * Pagination is a cursor, so this holds ONE page. No client-side search or sort,
 * for the same reason as the users list: filtering the rows in hand would search
 * one page and present the result as a search of the history.
 */
import { useState } from 'react';
import { useAdminAuditLog } from '@/hooks/useAdmin';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { AdminLoading, AdminError, AdminPageHeader } from './AdminAsync';
import type { AdminAuditEntry } from '@/types/api';

const PAGE_SIZE = 25;

function Entry({ entry }: { entry: AdminAuditEntry }) {
  const [open, setOpen] = useState(false);

  return (
    <li className="rounded-xl border border-white/[0.07] bg-white/[0.02] p-3.5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap gap-1.5">
          {entry.changedFields.length === 0 ? (
            <span className="rounded-lg border border-white/10 px-2 py-0.5 text-[11px] text-white/40">
              no fields recorded
            </span>
          ) : (
            entry.changedFields.map((f) => (
              <span
                key={f}
                className="rounded-lg border border-brand-teal/25 bg-brand-teal/[0.08] px-2 py-0.5 font-mono text-[11px] text-brand-teal"
              >
                {f}
              </span>
            ))
          )}
        </div>
        <span className="shrink-0 text-[11px] tabular-nums text-white/35">
          {new Date(entry.updatedAt).toLocaleString('en-AU')}
        </span>
      </div>

      {entry.note ? (
        <p className="mt-2 text-sm leading-relaxed text-white/70">{entry.note}</p>
      ) : (
        <p className="mt-2 text-sm italic text-white/30">No reason recorded</p>
      )}

      <div className="mt-2.5 flex items-center justify-between gap-3">
        <span className="text-xs text-white/40">
          by <span className="text-white/65">{entry.updatedBy}</span>
        </span>
        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-lg border border-white/10 px-2.5 py-1 text-[11px] font-medium text-white/50 transition hover:bg-white/[0.06] hover:text-white/80"
        >
          {open ? 'Hide the row as it stood' : 'Show the row as it stood'}
        </button>
      </div>

      {open && (
        <pre className="mt-3 max-h-80 overflow-auto rounded-lg border border-white/[0.07] bg-black/30 p-3 font-mono text-[11px] leading-relaxed text-white/60">
          {JSON.stringify(entry.snapshot, null, 2)}
        </pre>
      )}
    </li>
  );
}

export function AuditLogPage() {
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  /** Cursors already visited, so "Back" is a pop rather than a re-query from 0. */
  const [trail, setTrail] = useState<string[]>([]);

  const query = useAdminAuditLog({ limit: PAGE_SIZE, ...(cursor ? { cursor } : {}) });

  return (
    <>
      <AdminPageHeader
        title="Audit log"
        subtitle="Every write to the config row, newest first. Each entry carries the full row as it stood immediately after the change."
      />

      {query.isLoading ? (
        <AdminLoading label="Reading the log" />
      ) : query.isError || !query.data ? (
        <AdminError error={query.error} onRetry={() => void query.refetch()} />
      ) : (
        <>
          {query.data.items.length === 0 ? (
            <Card>
              <p className="text-sm text-white/40">
                Nothing recorded on this page. An empty log means no operator has written to the
                config row — the read succeeded.
              </p>
            </Card>
          ) : (
            <ul className="space-y-2.5">
              {query.data.items.map((entry) => (
                <Entry key={entry.id} entry={entry} />
              ))}
            </ul>
          )}

          <div className="mt-4 flex items-center justify-between gap-3">
            <span className="text-xs text-white/35">
              {query.data.items.length} change{query.data.items.length === 1 ? '' : 's'} on this
              page
              {query.data.nextCursor ? ' · more to follow' : ''}
            </span>
            <div className="flex gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={trail.length === 0}
                onClick={() => {
                  const next = [...trail];
                  const prev = next.pop();
                  setTrail(next);
                  setCursor(prev === '' ? undefined : prev);
                }}
              >
                Back
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!query.data.nextCursor}
                onClick={() => {
                  const nextCursor = query.data?.nextCursor;
                  if (!nextCursor) return;
                  setTrail((t) => [...t, cursor ?? '']);
                  setCursor(nextCursor);
                }}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
